// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SwiperInstance } from "@awc/App/Components/XngCarousel";
import { xClassNames } from "@awc/XDL/Utilities";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useState } from "react";

interface ArrowElementProps {
    swiper: SwiperInstance | null;
    arrowType: string;
    showOnMobile?: boolean;
}

interface State {
    className?: string;
    disabled?: boolean;
    element?: HTMLElement;
}

export function useArrowElement({ swiper, arrowType, showOnMobile }: ArrowElementProps) {
    const [options, setOptions] = useState<State>({
        className: "",
        element: undefined,
        disabled: true,
    });

    const debouncedUpdateArrowOptions = useCallback(
        debounce((swiper: SwiperInstance) => {
            updateArrowOptions(swiper);
        }, 100),
        [],
    );

    const updateArrowOptions = (swiper: SwiperInstance) => {
        const arrowElement = arrowType === "next" ? (swiper as any)?.navigation.nextEl : (swiper as any)?.navigation.prevEl;
        if (!arrowElement) {
            return;
        }

        const isDisabled = arrowElement.className.indexOf("swiper-button-disabled") > -1;

        const classNames = xClassNames.append([
            `arrow-button xng-carousel-${arrowType}`,
            showOnMobile ? "arrow-on-mobile" : "arrow-on-desktop",
            arrowElement.className.includes("swiper-button-lock") ? "swiper-button-lock" : "",
            isDisabled ? "swiper-button-disabled" : "",
        ]);

        setOptions({
            className: classNames,
            disabled: arrowElement.className.indexOf("swiper-button-disabled") > -1,
            element: arrowElement,
        });
    };

    useEffect(() => {
        if (!swiper) {
            return;
        }

        (swiper as any).on("slideChange", debouncedUpdateArrowOptions);
        (swiper as any).on("_slideClasses", debouncedUpdateArrowOptions);

        updateArrowOptions(swiper);

        return () => {
            (swiper as any).off("slideChange", debouncedUpdateArrowOptions);
            (swiper as any).off("_slideClasses", debouncedUpdateArrowOptions);
        };
    }, []);

    return options;
}
